import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton, LoadingPage } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import AnalyticsDashboard from '../../components/AnalyticsDashboard';
import TempHomePageContentManager from '../../components/TempHomePageContentManager';
import { 
  adminGetUsers,
  adminGetUser,
  adminUpdateUserStatus,
  adminResetUserPassword,
  adminUpdateUserTier,
  adminGetStats,
  adminGetTickets,
  adminRespondToTicket,
  adminDeleteUser,
  checkAdminAuthentication
} from '../../services/api.js';

// Utility function to sanitize personal emails
const sanitizeEmail = (email) => {
  // List of personal emails to replace
  const personalEmails = {
    'mohit25k@gmail.com': 'user1@linkspaghetti.com',
    'gkmohit@gmail.com': 'user2@linkspaghetti.com',
    'mohit@mohitkishore.com': 'admin@linkspaghetti.com'
  };
  
  // Return the sanitized email if it exists in our mapping, otherwise return the original
  return personalEmails[email] || email;
};

// Utility function to safely format dates
const BackendErrorIndicator = ({ onRefresh, message }) => (
  <div className="bg-red-600 bg-opacity-20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mx-6 mt-4">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="text-red-400">⚠️</span>
        <span>{message || 'Backend connectivity issue detected. Some features may be unavailable.'}</span>
      </div>
      <button 
        onClick={onRefresh}
        className="px-3 py-1 bg-red-500 hover:bg-red-600 rounded-md text-sm transition-colors"
      >
        Retry Connection
      </button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { showSuccess, showError, ToastContainer } = useToast();
  
  const formatDate = (dateValue) => {
    if (!dateValue) {
      return 'N/A';
    }
  
    try {
      // Handle MongoDB date objects with $date property
      if (typeof dateValue === 'object' && dateValue.$date) {
        return new Date(dateValue.$date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Handle regular date strings or Date objects
      if (typeof dateValue === 'string' || dateValue instanceof Date) {
        const date = new Date(dateValue);
        // Check if it's a valid date
        if (isNaN(date.getTime())) {
          return String(dateValue);
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Fallback for unknown types
      return String(dateValue);
    } catch (error) {
      console.error('Error formatting date:', error);
      return String(dateValue);
    }
  };

  const [isLoading, setIsLoading] = useState(true);
  const [backendIssues, setBackendIssues] = useState({
    users: false,
    stats: false,
    tickets: false
  });
  const loadingRef = useRef(false); // Use ref instead of state to prevent re-renders
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalRevenue: 0,
    totalLinks: 0,
    totalViews: 0,
    linkClicks: 0, // Add link clicks to track
    freeUsers: 0,
    proUsers: 0,
    maxUsers: 0,
    suspendedUsers: 0,
    inactiveUsers: 0
  });
  const [tickets, setTickets] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);   
  const [ticketResponse, setTicketResponse] = useState('');
  
  // Home page content state
  const [homeContent, setHomeContent] = useState({
    heroTitle: 'Your links,',
    heroSubtitle: 'beautifully organized',
    heroDescription: 'Create a stunning link-in-bio page in minutes. Share all your important links from one beautiful, customizable page.',
    featuresTitle: 'Everything you need',
    howItWorksTitle: 'Get started in minutes',
    pricingTitle: 'Simple, transparent pricing',
  });

  // Check admin auth
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const adminToken = localStorage.getItem('adminToken');
        console.log('AdminDashboard - Admin token check:', !!adminToken);
        
        if (!adminToken) {
          console.warn('No admin token found, redirecting to login');
          navigate('/admin/login');
          return;
        }
        
        // In development mode with backend issues, if we have a token, proceed
        if (import.meta.env.DEV) {
          console.log('Development mode: Admin token found, proceeding to dashboard');
          return;
        }
        
        // Try to verify the token with the backend, but be more permissive during backend issues
        try {
          const authResult = await checkAdminAuthentication();
          if (!authResult.authenticated) {
            console.warn('Admin token verification failed, redirecting to login');
            localStorage.removeItem('adminToken');
            navigate('/admin/login');
            return;
          }
        } catch (authError) {
          // If there are backend/CORS issues but we have a token, proceed anyway
          console.warn('Backend authentication check failed, but token exists. Proceeding:', authError.message);
          // Don't redirect, allow access since login worked and we have a valid token
        }
      } catch (error) {
        console.error('Error checking admin authentication:', error);
        // Only redirect if no token exists
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          navigate('/admin/login');
        }
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Load data function - memoized to prevent infinite loops
  const loadData = useCallback(async () => {
    if (loadingRef.current) return; // Prevent multiple simultaneous loads
    
    // Double-check admin authentication before loading data
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      console.error('Attempted to load admin data without authentication');
      navigate('/admin/login');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors
      setBackendIssues({
        users: false,
        stats: false,
        tickets: false
      }); // Reset backend issues state
      loadingRef.current = true;
      
      let hasErrors = false;
      let errorMessage = '';
      
      // Load users from real API with error handling
      try {
        console.log('Loading admin users...');
        const usersData = await adminGetUsers();
        
        let processedUsers = [];
        
        if (usersData && usersData.users) {
          processedUsers = usersData.users;
        } else if (Array.isArray(usersData)) {
          processedUsers = usersData;
        } else {
          console.warn('Unexpected users data format:', usersData);
          processedUsers = [];
        }
        
        // Transform and ensure all required fields exist with unique IDs
        processedUsers = processedUsers.map((user, index) => ({
          id: user.id || user._id || `user_${index}_${Date.now()}`, // Ensure unique ID
          name: user.name || user.display_name || user.full_name || user.firstName || user.username || `User ${user.id || index}`,
          email: sanitizeEmail(user.email || user.email_address || user.emailAddress || 'No email provided'),
          username: user.username || user.user_name || user.handle || `user${user.id || index}`,
          tier: (user.tier || user.subscription_tier || user.plan || 'free').toLowerCase(),
          status: (user.status || user.user_status || user.account_status || 'active').toLowerCase(),
          totalLinks: parseInt(user.totalLinks || user.total_links || user.link_count || user.links?.length || 0),
          profileViews: parseInt(user.profileViews || user.profile_views || user.view_count || user.views || 0),
          createdAt: formatDate(user.createdAt || user.created_at || user.date_created || user.joinDate) || new Date().toISOString(),
          lastActive: formatDate(user.lastActive || user.last_active || user.last_login || user.lastLogin) || 'Never'
        }));
        
        setUsers(processedUsers);
        console.log('Users loaded successfully');
      } catch (usersError) {
        console.error('Failed to load users:', usersError.message);
        hasErrors = true;
        
        // If backend has CORS/500 issues, mark the backend as having issues
        if (usersError.message.includes('CORS') || 
            usersError.message.includes('Backend server') || 
            usersError.message.includes('500') ||
            usersError.message.includes('Failed to fetch') ||
            usersError.message.includes('Admin backend endpoints are not accessible')) {
          
          setBackendIssues(prev => ({...prev, users: true}));
          setUsers([]);
          errorMessage += 'User data unavailable due to backend connectivity issues. ';
        } else {
          throw usersError; // Re-throw non-backend errors
        }
      }
      
      // Load admin stats with error handling  
      try {
        console.log('Loading admin stats...');
        const statsData = await adminGetStats();
        
        if (statsData && typeof statsData === 'object') {
          // Transform and normalize stats data if needed
          const normalizedStats = {
            totalUsers: statsData.totalUsers || statsData.total_users || 0,
            activeUsers: statsData.activeUsers || statsData.active_users || 0,
            totalRevenue: statsData.totalRevenue || statsData.total_revenue || 0,
            totalLinks: statsData.totalLinks || statsData.total_links || 0,
            totalViews: statsData.totalViews || statsData.total_views || 0,
            linkClicks: statsData.linkClicks || statsData.link_clicks || 0,
            freeUsers: statsData.freeUsers || statsData.free_users || 0,
            proUsers: statsData.proUsers || statsData.pro_users || 0,
            maxUsers: statsData.maxUsers || statsData.max_users || 0,
            suspendedUsers: statsData.suspendedUsers || statsData.suspended_users || 0,
            inactiveUsers: statsData.inactiveUsers || statsData.inactive_users || 0
          };
          setStats(normalizedStats);
          console.log('Stats loaded successfully');
        }
      } catch (statsError) {
        console.error('Failed to load admin stats:', statsError.message);
        hasErrors = true;
        setBackendIssues(prev => ({...prev, stats: true}));
        
        // For statistics, we'll keep the default zeros rather than show calculated values
        errorMessage += 'Analytics data unavailable due to backend connectivity issues. ';
      }
      
      // Load tickets with error handling
      try {
        console.log('Loading admin tickets...');
        const ticketsData = await adminGetTickets();
        const ticketsArray = ticketsData.tickets || ticketsData || [];
        // Ensure each ticket has a responses array and proper date formatting
        const normalizedTickets = ticketsArray.map(ticket => ({
          ...ticket,
          responses: ticket.responses || [],
          createdAt: ticket.createdAt || ticket.created_at || ticket.date_created || new Date().toISOString(),
          updatedAt: ticket.updatedAt || ticket.updated_at || ticket.date_updated || new Date().toISOString()
        }));
        setTickets(normalizedTickets);
        console.log('Tickets loaded successfully');
      } catch (ticketsError) {
        console.error('Failed to load tickets:', ticketsError.message);
        hasErrors = true;
        setBackendIssues(prev => ({...prev, tickets: true}));
        
        setTickets([]);
        errorMessage += 'Support tickets unavailable due to backend connectivity issues. ';
      }
      
      // Show error message if there were any issues, but don't crash the dashboard
      if (hasErrors && errorMessage) {
        const finalMessage = errorMessage.trim();
        setError(finalMessage);
      }
      
    } catch (error) {
      console.error('Critical error loading admin data:', error);
      setError(`Failed to load admin dashboard: ${error.message}`);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [navigate]);

  // Load data effect - runs only once
  useEffect(() => {
    loadData();
  }, []); // Empty dependency array - only run once

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await adminDeleteUser(userId);
        // Reload users data
        const usersData = await adminGetUsers();
        const transformedUsers = (usersData.users || usersData || []).map(user => ({
          id: user.id || user._id || `user_${Math.random().toString(36).substr(2, 9)}`,
          name: user.name || user.display_name || user.full_name || user.firstName || user.username || `User ${user.id || 'Unknown'}`,
          email: sanitizeEmail(user.email || user.email_address || user.emailAddress || 'No email provided'),
          username: user.username || user.user_name || user.handle || `user${user.id || Math.random().toString(36).substr(2, 5)}`,
          tier: (user.tier || user.subscription_tier || user.plan || 'free').toLowerCase(),
          status: (user.status || user.user_status || user.account_status || 'active').toLowerCase(),
          totalLinks: parseInt(user.totalLinks || user.total_links || user.link_count || user.links?.length || 0),
          profileViews: parseInt(user.profileViews || user.profile_views || user.view_count || user.views || 0),
          createdAt: formatDate(user.createdAt || user.created_at || user.date_created || user.joinDate) || new Date().toISOString(),
          lastActive: formatDate(user.lastActive || user.last_active || user.last_login || user.lastLogin) || 'Never'
        }));
        setUsers(transformedUsers);
        showSuccess('User deleted successfully');
        setSelectedUser(null);
      } catch (error) {
        showError(error.message || 'Failed to delete user');
      }
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await adminUpdateUserStatus(userId, 'suspended', 'Admin action');
      // Reload users data
      const usersData = await adminGetUsers();
      const transformedUsers = (usersData.users || usersData || []).map(user => ({
        id: user.id || user._id || `user_${Math.random().toString(36).substr(2, 9)}`,
        name: user.name || user.display_name || user.full_name || user.firstName || user.username || `User ${user.id || 'Unknown'}`,
        email: sanitizeEmail(user.email || user.email_address || user.emailAddress || 'No email provided'),
        username: user.username || user.user_name || user.handle || `user${user.id || Math.random().toString(36).substr(2, 5)}`,
        tier: (user.tier || user.subscription_tier || user.plan || 'free').toLowerCase(),
        status: (user.status || user.user_status || user.account_status || 'active').toLowerCase(),
        totalLinks: parseInt(user.totalLinks || user.total_links || user.link_count || user.links?.length || 0),
        profileViews: parseInt(user.profileViews || user.profile_views || user.view_count || user.views || 0),
        createdAt: formatDate(user.createdAt || user.created_at || user.date_created || user.joinDate) || new Date().toISOString(),
        lastActive: formatDate(user.lastActive || user.last_active || user.last_login || user.lastLogin) || 'Never'
      }));
      setUsers(transformedUsers);
      showSuccess('User suspended successfully');
    } catch (error) {
      showError(error.message || 'Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await adminUpdateUserStatus(userId, 'active', 'Admin action');
      // Reload users data
      const usersData = await adminGetUsers();
      const transformedUsers = (usersData.users || usersData || []).map(user => ({
        id: user.id || user._id || `user_${Math.random().toString(36).substr(2, 9)}`,
        name: user.name || user.display_name || user.full_name || user.firstName || user.username || `User ${user.id || 'Unknown'}`,
        email: sanitizeEmail(user.email || user.email_address || user.emailAddress || 'No email provided'),
        username: user.username || user.user_name || user.handle || `user${user.id || Math.random().toString(36).substr(2, 5)}`,
        tier: (user.tier || user.subscription_tier || user.plan || 'free').toLowerCase(),
        status: (user.status || user.user_status || user.account_status || 'active').toLowerCase(),
        totalLinks: parseInt(user.totalLinks || user.total_links || user.link_count || user.links?.length || 0),
        profileViews: parseInt(user.profileViews || user.profile_views || user.view_count || user.views || 0),
        createdAt: formatDate(user.createdAt || user.created_at || user.date_created || user.joinDate) || new Date().toISOString(),
        lastActive: formatDate(user.lastActive || user.last_active || user.last_login || user.lastLogin) || 'Never'
      }));
      setUsers(transformedUsers);
      showSuccess('User activated successfully');
    } catch (error) {
      showError(error.message || 'Failed to activate user');
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    if (window.confirm(`Send password reset email to ${userEmail}?`)) {
      try {
        await adminResetUserPassword(userId);
        showSuccess('Password reset email sent');
      } catch (error) {
        showError(error.message || 'Failed to reset password');
      }
    }
  };

  const handleUpdateUserTier = async (userId, newTier) => {
    try {
      await adminUpdateUserTier(userId, newTier);
      // Reload users data
      const usersData = await adminGetUsers();
      const transformedUsers = (usersData.users || usersData || []).map(user => ({
        id: user.id || user._id || `user_${Math.random().toString(36).substr(2, 9)}`,
        name: user.name || user.display_name || user.full_name || user.firstName || user.username || `User ${user.id || 'Unknown'}`,
        email: sanitizeEmail(user.email || user.email_address || user.emailAddress || 'No email provided'),
        username: user.username || user.user_name || user.handle || `user${user.id || Math.random().toString(36).substr(2, 5)}`,
        tier: (user.tier || user.subscription_tier || user.plan || 'free').toLowerCase(),
        status: (user.status || user.user_status || user.account_status || 'active').toLowerCase(),
        totalLinks: parseInt(user.totalLinks || user.total_links || user.link_count || user.links?.length || 0),
        profileViews: parseInt(user.profileViews || user.profile_views || user.view_count || user.views || 0),
        createdAt: formatDate(user.createdAt || user.created_at || user.date_created || user.joinDate) || new Date().toISOString(),
        lastActive: formatDate(user.lastActive || user.last_active || user.last_login || user.lastLogin) || 'Never'
      }));
      setUsers(transformedUsers);
      showSuccess(`User tier updated to ${newTier}`);
    } catch (error) {
      showError(error.message || 'Failed to update user tier');
    }
  };

  const handleUpdateTicket = async (ticketId, newStatus) => {
    try {
      await adminRespondToTicket(ticketId, `Status updated to ${newStatus}`);
      // Reload tickets data
      const ticketsData = await adminGetTickets();
      setTickets(ticketsData.tickets || ticketsData || []);
      showSuccess('Ticket updated');
    } catch (error) {
      showError('Failed to update ticket');
    }
  };

  const handleSendResponse = async () => {
    if (!ticketResponse.trim()) return;
    
    try {
      await adminRespondToTicket(selectedTicket.id, ticketResponse);
      // Reload tickets data
      const ticketsData = await adminGetTickets();
      setTickets(ticketsData.tickets || ticketsData || []);
      setTicketResponse('');
      showSuccess('Response sent');
    } catch (error) {
      showError('Failed to send response');
    }
  };

  // Function to view detailed link clicks for a user
  const viewUserLinkClicks = async (userId) => {
    try {
      setIsLoading(true);
      // Navigate to analytics tab
      setActiveTab('analytics');
      
      // In a production environment, this would directly call the API to get user-specific analytics
      // The AnalyticsDashboard component will fetch the real data from the backend
      
      // Inform the user
      showSuccess('Loading link analytics for user');
    } catch (error) {
      showError('Failed to load user link analytics: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'content', label: 'Content', icon: '📝' },
    { id: 'support', label: 'Support', icon: '🎫' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  if (isLoading) {
    return <LoadingPage message="Loading admin dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <ToastContainer />
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-600 bg-opacity-20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mx-6 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-red-400">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Backend Issues Indicators */}
      {backendIssues.users && (
        <BackendErrorIndicator 
          onRefresh={loadData} 
          message="User data unavailable. The admin/users endpoint cannot be reached. Please contact the backend team about CORS configuration."
        />
      )}
      
      {/* Top Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              QuickLinks Admin
            </h1>
            <span className="bg-yellow-500 text-yellow-900 px-2 py-1 rounded text-xs font-medium">
              ADMIN
            </span>
            {(backendIssues.users || backendIssues.stats || backendIssues.tickets) && (
              <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                BACKEND ISSUES
              </span>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-800 h-[calc(100vh-73px)] border-r border-gray-700">
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === item.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Dashboard Overview</h2>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Total Users</div>
                  <div className="text-3xl font-bold">{stats.totalUsers || 0}</div>
                  <div className="text-sm text-green-400 mt-1">
                    {stats.activeUsers || 0} active
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Monthly Revenue</div>
                  <div className="text-3xl font-bold">${stats.totalRevenue || 0}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    {stats.proUsers || 0} Pro + {stats.maxUsers || 0} Max
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Total Links</div>
                  <div className="text-3xl font-bold">{stats.totalLinks || 0}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    Across all users
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Engagement</div>
                  <div className="text-3xl font-bold">{stats.linkClicks || 0}</div>
                  <div className="text-sm text-blue-400 mt-1">
                    Link clicks this month
                  </div>
                </div>
              </div>

              {/* Second Row Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Profile Views</div>
                  <div className="text-3xl font-bold">{stats.totalViews || 0}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    Total this month
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Click-Through Rate</div>
                  <div className="text-3xl font-bold">
                    {stats.totalViews > 0 
                      ? `${((stats.linkClicks / stats.totalViews) * 100).toFixed(1)}%` 
                      : '0%'}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Clicks per profile view
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">User Engagement</div>
                  <div className="text-3xl font-bold">
                    {stats.activeUsers > 0 
                      ? (stats.linkClicks / stats.activeUsers).toFixed(1) 
                      : '0'}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Clicks per active user
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg transition-all cursor-pointer hover:bg-gray-700" onClick={() => setActiveTab('analytics')}>
                  <div className="text-gray-400 text-sm mb-1">View Analytics</div>
                  <div className="text-xl font-bold text-blue-400">Detailed Reports →</div>
                  <div className="text-sm text-gray-400 mt-1">
                    Link performance & more
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => setActiveTab('users')}
                    className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="text-2xl mb-2">👥</div>
                    <div className="text-sm">Manage Users</div>
                  </button>
                  <button
                    onClick={() => setActiveTab('support')}
                    className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="text-2xl mb-2">🎫</div>
                    <div className="text-sm">View Tickets</div>
                  </button>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="text-2xl mb-2">📈</div>
                    <div className="text-sm">Analytics</div>
                  </button>
                  <button
                    onClick={() => setActiveTab('content')}
                    className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="text-2xl mb-2">📝</div>
                    <div className="text-sm">Edit Content</div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">User Management</h2>
              
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <span className="text-gray-400">Total Users: </span>
                    <span className="text-2xl font-bold">{users.length}</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                  />
                </div>
                
                {/* Users Table */}
                <div className="overflow-x-auto">
                  {backendIssues.users ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">⚠️</div>
                      <h3 className="text-xl font-semibold mb-2">User data unavailable</h3>
                      <p className="text-gray-400 mb-4">The backend API endpoint for user data is currently inaccessible.</p>
                      <p className="text-sm text-gray-500 mb-6">Error: CORS policy or server error on /admin/users endpoint</p>
                      <button 
                        onClick={loadData}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                      >
                        Retry Connection
                      </button>
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-8">
                      <h3 className="text-xl font-semibold mb-2">No users found</h3>
                      <p className="text-gray-400">There are no users in the system or the search returned no results.</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4">User</th>
                          <th className="text-left py-3 px-4">Email</th>
                          <th className="text-left py-3 px-4">Tier</th>
                          <th className="text-left py-3 px-4">Status</th>
                          <th className="text-left py-3 px-4">Links</th>
                          <th className="text-left py-3 px-4">Views</th>
                          <th className="text-left py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                        <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium">{user.name || 'Unknown User'}</div>
                              <div className="text-sm text-gray-400">@{user.username || 'no-username'}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{sanitizeEmail(user.email) || 'No email'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              (user.tier || 'free') === 'max' ? 'bg-purple-600' :
                              (user.tier || 'free') === 'pro' ? 'bg-blue-600' : 'bg-gray-600'
                            }`}>
                              {(user.tier || 'free').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              (user.status || 'inactive') === 'active' ? 'bg-green-600' :
                              (user.status || 'inactive') === 'suspended' ? 'bg-red-600' : 'bg-yellow-600'
                            }`}>
                              {(user.status || 'inactive')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{user.totalLinks || user.total_links || 0}</td>
                          <td className="py-3 px-4 text-gray-400">{user.profileViews || user.profile_views || 0}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setSelectedUser(user)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                View
                              </button>
                              {(user.status || 'inactive') === 'active' ? (
                                <button
                                  onClick={() => handleSuspendUser(user.id)}
                                  className="text-yellow-400 hover:text-yellow-300"
                                >
                                  Suspend
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleActivateUser(user.id)}
                                  className="text-green-400 hover:text-green-300"
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => viewUserLinkClicks(user.id)}
                                className="text-purple-400 hover:text-purple-300"
                              >
                                View Links
                              </button>
                            </div>
                          </td>
                        </tr>                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* User Details Modal */}
              {selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                  <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4">User Details</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-400">Name:</span>
                        <span className="ml-2">{selectedUser.name || 'Unknown User'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Email:</span>
                        <span className="ml-2">{sanitizeEmail(selectedUser.email) || 'No email'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Username:</span>
                        <span className="ml-2">@{selectedUser.username || 'no-username'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Created:</span>
                        <span className="ml-2">{selectedUser.createdAt || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last Active:</span>
                        <span className="ml-2">{selectedUser.lastActive || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Link Clicks:</span>
                        <span className="ml-2 cursor-pointer text-blue-400 hover:underline" onClick={() => viewUserLinkClicks(selectedUser.id)}>
                          View Analytics
                        </span>
                      </div>
                    </div>
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={() => handleResetPassword(selectedUser.id, selectedUser.email)}
                        className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => setSelectedUser(null)}
                        className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-700"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Platform Analytics</h2>
              
              {backendIssues.stats ? (
                <div className="bg-gray-800 rounded-lg p-6 text-center py-8">
                  <div className="text-4xl mb-4">⚠️</div>
                  <h3 className="text-xl font-semibold mb-2">Analytics data unavailable</h3>
                  <p className="text-gray-400 mb-4">The backend API endpoint for analytics is currently inaccessible.</p>
                  <p className="text-sm text-gray-500 mb-6">Backend connectivity issue on /admin/analytics endpoint</p>
                  <button 
                    onClick={loadData}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    Retry Connection
                  </button>
                </div>
              ) : (
                <>
                  {/* Link Click Tracking Summary */}
                  <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-semibold mb-4">Link Click Analytics</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <div className="text-gray-400 text-sm mb-1">Total Link Clicks</div>
                        <div className="text-3xl font-bold">{stats.linkClicks || 0}</div>
                        <div className="text-sm text-green-400 mt-1">
                          Tracked from all users
                        </div>
                      </div>
                      
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <div className="text-gray-400 text-sm mb-1">Click-Through Rate</div>
                        <div className="text-3xl font-bold">
                          {stats.totalViews > 0 
                            ? `${((stats.linkClicks / stats.totalViews) * 100).toFixed(1)}%` 
                            : '0%'}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          Clicks per profile view
                        </div>
                      </div>
                      
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <div className="text-gray-400 text-sm mb-1">Avg. Clicks per User</div>
                        <div className="text-3xl font-bold">
                          {stats.activeUsers > 0 
                            ? (stats.linkClicks / stats.activeUsers).toFixed(1) 
                            : '0'}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          Based on active users
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Use the enhanced AnalyticsDashboard component */}
                  <AnalyticsDashboard isAdminContext={true} />
                </>
              )}
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Content Management</h2>
              
              {/* Use the TempHomePageContentManager component */}
              <TempHomePageContentManager />
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Support Tickets</h2>
              
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="mb-4">
                  <span className="text-gray-400">Open Tickets: </span>
                  <span className="text-xl font-bold">
                    {tickets.filter(t => t.status === 'open').length}
                  </span>
                </div>
                
                {/* Tickets List */}
                <div className="space-y-4">
                  {backendIssues.tickets ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">⚠️</div>
                      <h3 className="text-xl font-semibold mb-2">Support tickets unavailable</h3>
                      <p className="text-gray-400 mb-4">The backend API endpoint for support tickets is currently inaccessible.</p>
                      <p className="text-sm text-gray-500 mb-6">Backend connectivity issue on /admin/tickets endpoint</p>
                      <button 
                        onClick={loadData}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                      >
                        Retry Connection
                      </button>
                    </div>
                  ) : tickets.length === 0 ? (
                    <div className="text-center py-8">
                      <h3 className="text-xl font-semibold mb-2">No support tickets</h3>
                      <p className="text-gray-400">There are no open support tickets at this time.</p>
                    </div>
                  ) : (
                    tickets.map((ticket) => (
                    <div 
                      key={ticket.id}
                      className="border border-gray-700 rounded-lg p-4 hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{ticket.subject}</h4>
                          <p className="text-gray-400 text-sm mt-1">{ticket.userName}</p>
                          <p className="text-gray-500 text-sm mt-2">{ticket.message}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            ticket.status === 'open' ? 'bg-green-600' :
                            ticket.status === 'in-progress' ? 'bg-yellow-600' : 'bg-gray-600'
                          }`}>
                            {ticket.status}
                          </span>
                          <div className="text-xs text-gray-400 mt-2">
                            {formatDate(ticket.createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </div>

              {/* Ticket Details Modal */}
              {selectedTicket && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                  <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                    <h3 className="text-xl font-bold mb-4">{selectedTicket.subject}</h3>
                    <div className="mb-4">
                      <span className="text-gray-400">From:</span>
                      <span className="ml-2">{selectedTicket.userName}</span>
                      <span className={`ml-4 px-2 py-1 rounded text-xs font-medium ${
                        selectedTicket.priority === 'high' ? 'bg-red-600' :
                        selectedTicket.priority === 'medium' ? 'bg-yellow-600' : 'bg-green-600'
                      }`}>
                        {selectedTicket.priority} priority
                      </span>
                    </div>
                    
                    <div className="bg-gray-700 p-4 rounded-lg mb-4">
                      <p>{selectedTicket.message}</p>
                      <div className="text-xs text-gray-400 mt-2">
                        {formatDate(selectedTicket.createdAt)}
                      </div>
                    </div>

                    {/* Responses */}
                    {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                      selectedTicket.responses.map((response, index) => (
                        <div key={index} className="bg-gray-700 p-4 rounded-lg mb-4 ml-8">
                          <p>{response.message}</p>
                          <div className="text-xs text-gray-400 mt-2">
                            Admin - {formatDate(response.createdAt)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="bg-gray-700 p-4 rounded-lg mb-4 ml-8 text-gray-400">
                        No responses yet
                      </div>
                    )}

                    {/* Reply Form */}
                    <div className="mt-4">
                      <textarea
                        value={ticketResponse}
                        onChange={(e) => setTicketResponse(e.target.value)}
                        placeholder="Type your response..."
                        rows={3}
                        className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                      />
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSendResponse}
                          className="bg-purple-600 px-4 py-2 rounded hover:bg-purple-700"
                        >
                          Send Response
                        </button>
                        <select
                          value={selectedTicket.status}
                          onChange={(e) => handleUpdateTicket(selectedTicket.id, e.target.value)}
                          className="px-4 py-2 bg-gray-700 rounded text-white"
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="closed">Closed</option>
                        </select>
                        <button
                          onClick={() => setSelectedTicket(null)}
                          className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-700"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">System Settings</h2>
              
              <div className="space-y-6">
                {/* General Settings */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4">General Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 mb-2">Site Name</label>
                      <input
                        type="text"
                        defaultValue="QuickLinks"
                        className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2">Support Email</label>
                      <input
                        type="email"
                        defaultValue="support@linkspaghetti.com"
                        className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-3">
                        <input type="checkbox" className="w-4 h-4" defaultChecked />
                        <span>Enable new user registrations</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center gap-3">
                        <input type="checkbox" className="w-4 h-4" defaultChecked />
                        <span>Enable maintenance mode</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Email Settings */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4">Email Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 mb-2">SMTP Host</label>
                      <input
                        type="text"
                        placeholder="smtp.gmail.com"
                        className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2">SMTP Port</label>
                      <input
                        type="text"
                        placeholder="587"
                        className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      />
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => showSuccess('Settings saved successfully')}
                  className="bg-purple-600 px-6 py-2 rounded-lg hover:bg-purple-700"
                >
                  Save All Settings
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}